import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { filterRepairJobs, type RepairJob } from '@/data/repairJobs';
import { useI18n } from '@/i18n/LanguageContext';

interface WorkItemAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (job: RepairJob) => void;
  placeholder?: string;
  className?: string;
}

export const WorkItemAutocomplete: React.FC<WorkItemAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder,
  className = '',
}) => {
  const { language } = useI18n();
  const [suggestions, setSuggestions] = useState<RepairJob[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Track if we just selected an item to prevent immediate re-showing
  const justSelectedRef = useRef(false);

  const updateDropdownPosition = () => {
    const input = inputRef.current;
    if (!input) return;
    const rect = input.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  };

  // When dropdown opens, position it before paint; keep position in sync on scroll/resize
  useLayoutEffect(() => {
    if (!showSuggestions || suggestions.length === 0) {
      setDropdownPosition(null);
      return;
    }
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showSuggestions, suggestions.length]);

  // Filter suggestions when value changes
  useEffect(() => {
    // If we just selected an item, don't show suggestions immediately
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      return;
    }

    if (value.length >= 3 && !isComposing) {
      const filtered = filterRepairJobs(value, language as 'en' | 'lv');
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  }, [value, language, isComposing]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelect = (job: RepairJob) => {
    const selectedValue = job[language as 'en' | 'lv'];
    justSelectedRef.current = true;
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setSuggestions([]);
    onChange(selectedValue);
    // Call onSelect callback if provided
    if (onSelect) {
      onSelect(job);
    }
    // Blur input to close autocomplete
    setTimeout(() => {
      inputRef.current?.blur();
    }, 0);
  };

  // Close dropdown when clicking outside (input container or portal dropdown)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inInput = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inInput && !inDropdown) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  // Handle composition events (for IME input like Chinese, Japanese, etc.)
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onFocus={() => {
          if (value.length >= 3 && suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        className={className}
        style={{ fontSize: '0.875rem' }}
        autoComplete="off"
      />
      
      {showSuggestions && suggestions.length > 0 && dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
            style={{
              top: dropdownPosition.top + 4,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {suggestions.map((job, index) => (
              <button
                key={job.id}
                type="button"
                onClick={() => handleSelect(job)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                  index === selectedIndex ? 'bg-gray-100' : ''
                }`}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {job[language as 'en' | 'lv']}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

